import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardMedia,
  Grid,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';


import CompletedPuzzle from '../../assets/images/slideshow5.jpg';
import '../../App.css';

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut', delay: d } },
});

const Products = () => {
  const navigate = useNavigate();

  const product = {
    id: 1,
    title: 'Braille Skills Puzzle',
    description:
      'A fun, engaging way for users of all ages to keep their tactile skills sharp. At the same time, users can develop and retain essential Braille skills.',
    price: 59.99,
    weightOz: 16,
    img: CompletedPuzzle,
  };



  const handleContactUs = () => {
    navigate('/contact');
    if (window.gtag) {
      window.gtag('event', 'contact_us_click', {
        event_category: 'Engagement',
        event_label: 'Product Page Contact',
      });
    }
  };



  const viewDetails = () => {
    navigate('/product-detail');
    if (window.gtag) {
      window.gtag('event', 'view_item', {
        event_category: 'Ecommerce',
        event_label: product.title,
      });
    }
  };

  const priceLabel = `$${product.price.toFixed(2)}`;
  const weightLabel =
    product.weightOz >= 16 ? `${(product.weightOz / 16).toFixed(1)} lb` : `${product.weightOz} oz`;

  return (
    <Container disableGutters sx={{ maxWidth: '100% !important', p: 0 }}>
      <Helmet>
        <title>Products | Amaze Puzzles</title>
        <meta
          name="description"
          content="Discover the Braille Skills Puzzle — a modern, play-based tool designed to enhance Braille literacy."
        />
      </Helmet>

      {/* Product body */}
      <Container
        maxWidth="lg"
        sx={{ padding: '25px !important', px: { xs: 3, sm: 5, md: 6 }, py: { xs: 6, md: 10 } }}
      >
        {/* ONE unified card for image + content */}
        <Card
          component={motion.section}
          {...fadeUp(0.05)}
          aria-labelledby="product-title"
          sx={{
            borderRadius: 4,
            border: '1px solid var(--card-border)',
            backgroundImage: 'var(--card-background-gradient)',
            backdropFilter: 'blur(8px)',
            boxShadow: 'var(--card-shadow)',
            p: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 2, md: 4 },
              alignItems: 'center',
            }}
          >
            {/* Image */}
            <Box sx={{ p: { xs: 1, md: 2 } }}>
              <CardMedia
                component="img"
                alt={product.title}
                image={product.img}
                title={product.title}
                sx={{
                  width: '100%',
                  height: { xs: 360, md: 520 },
                  objectFit: 'contain',
                  borderRadius: 2,
                  backgroundColor: 'var(--product-image-bg)',
                }}
              />
            </Box>

            {/* Content */}
            <Box sx={{ p: { xs: 1, md: 1 } }}>
              <Typography
                id="product-title"
                variant="h4"
                sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}
              >
                {product.title}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Chip label="Play-based learning" size="small" />
                <Chip label="Accessible & tactile" size="small" />
                <Chip label="Durable build" size="small" />
              </Stack>

              <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mb: 2 }}>
                {product.description}
              </Typography>

              <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', mb: 1 }}>
                Colors may vary.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)' }}>
                  {priceLabel}
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <Button
                  onClick={handleContactUs}
                  variant="contained"
                  size="large"
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    py: 1.25,
                    background: 'var(--button-gradient-primary)',
                    '&:hover': { filter: 'brightness(0.95)' },
                  }}
                  aria-label="Contact us about this product"
                >
                  Contact Us
                </Button>



                <Button
                  onClick={viewDetails}
                  variant="outlined"
                  size="large"
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    py: 1.25,
                    borderColor: 'var(--button-outline-border)',
                    color: 'var(--button-outline-text)',
                    '&:hover': { borderColor: 'var(--button-outline-border-hover)', background: 'var(--button-outline-bg-hover)' },
                  }}
                  aria-label="View product details"
                >
                  View Details
                </Button>
              </Stack>
            </Box>
          </Box>
        </Card>

        {/* Info stripes */}
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mt: { xs: 6, md: 10 } }}>
          <Grid item xs={12} md={4}>
            <Card
              component={motion.div}
              {...fadeUp(0.15)}
              sx={{
                borderRadius: 3,
                p: 3,
                border: '1px solid var(--card-border)',
                backgroundImage: 'var(--card-background-gradient)',
                backdropFilter: 'blur(8px)',
                height: '100%',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Why it works
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                Tactile, game-like practice turns repetition into motivation — helping learners build
                confidence and retain Braille skills longer.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              component={motion.div}
              {...fadeUp(0.2)}
              sx={{
                borderRadius: 3,
                p: 3,
                border: '1px solid var(--card-border)',
                backgroundImage: 'var(--card-background-gradient)',
                backdropFilter: 'blur(8px)',
                height: '100%',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Great for
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                Classrooms, resource rooms, and at-home practice. Designed for diverse learners and
                adaptable to different goals.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              component={motion.div}
              {...fadeUp(0.25)}
              sx={{
                borderRadius: 3,
                p: 3,
                border: '1px solid var(--card-border)',
                backgroundImage: 'var(--card-background-gradient)',
                backdropFilter: 'blur(8px)',
                height: '100%',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                What’s inside
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                Puzzle board and tactile pieces, plus a quick-start guide. <em>(Contents and colors may vary.)</em>
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Container>
  );
};

export default Products;
